import { useMemo } from "react";
import { useScopedActivityWhere } from "#app/widgets/dashboards/scope/use-scoped-activity-where";
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import { ARCHIVED_OUT } from "#app/widgets/dashboards/use-dimension-slices";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import { useActivityGroupCounts } from "../shared/use-activity-group-counts";
import { useTeamMembersCard } from "./use-team-members-card";
import type { TeamMember } from "#app/lib/hooks/use-team-members";

export type MemberBreakdown = {
  id: string;
  label: string;
  count: number;
};

export type TeamMemberInsight = {
  member: TeamMember;
  recentUpdates: number;
  updatedProjects: number;
};

/**
 * Real-data reader for the expandable team roster. EVERY number is an exact
 * server aggregation over the dashboard window — nothing derives from a capped
 * sample: `recentUpdates` counts each member's activity events (per-actor
 * activity aggregation), and `updatedProjects` counts their assigned projects
 * updated in the period (issue counts grouped by assignee over a
 * `lastUpdateDate` window). The expanded member's project rows are NOT read
 * here — `useMemberProjects` gives each expanded member its own windowed paged
 * query, so the table reaches every assigned project. The per-(member, project)
 * event counts feed the expanded table's "Recent activity" column.
 */
export function useTeamMemberInsights(leadsFirst: boolean): {
  isTeamScope: boolean;
  insights: TeamMemberInsight[];
  /** Per-(member, project) event counts, keyed `"<userId>:<issueId>"` by the server. */
  updatesByMemberProject: Record<string, number>;
  loading: boolean;
} {
  const { isTeamScope, members, loading: membersLoading } = useTeamMembersCard(leadsFirst);
  const { where: activityWhere } = useScopedActivityWhere();
  const { where: projectWhere } = useScopedWhere(ARCHIVED_OUT, {
    dateField: "lastUpdateDate",
  });

  const { counts: updatesByMember, ready: updatesReady } = useActivityGroupCounts({
    groupBy: "actor",
    where: activityWhere,
    skip: !isTeamScope,
  });
  // Per-(member, project) event counts for the expanded roster's "Recent
  // activity" column — keyed "<userId>:<issueId>" by the server.
  const { counts: updatesByMemberProject, ready: perProjectReady } = useActivityGroupCounts({
    groupBy: "actor.issue",
    where: activityWhere,
    skip: !isTeamScope,
  });
  const { counts: updatedProjectsByMember, ready: projectCountsReady } = useIssueGroupCounts({
    where: isTeamScope ? projectWhere : undefined,
    groupBy: "assignees.entityId",
  });

  const insights = useMemo<TeamMemberInsight[]>(
    () =>
      members.map((member) => ({
        member,
        recentUpdates: updatesByMember[member.userId] ?? 0,
        updatedProjects: updatedProjectsByMember[member.userId] ?? 0,
      })),
    [members, updatesByMember, updatedProjectsByMember],
  );

  return {
    isTeamScope,
    insights,
    updatesByMemberProject,
    loading:
      membersLoading ||
      (isTeamScope && (!updatesReady || !perProjectReady || !projectCountsReady)),
  };
}
